import { useTheme } from "@mui/material";
import { FrontSide } from "three";

interface GridShaderMaterialProps {
  animationSpeed?: number;
  baseWidth: number;
  maxWidth: number;
  gridSize: number;
  opacity: number;
  grid: number;
}

export const GridShaderMaterial = ({
  animationSpeed,
  baseWidth,
  maxWidth,
  opacity,
  gridSize,
  grid,
}: GridShaderMaterialProps) => {
  const theme = useTheme();
  return (
    <shaderMaterial
      fragmentShader={`
    varying vec2 vUv;
    uniform vec3 color;
    uniform float time;
    
    void main() {
      // Create animated grid pattern by offsetting UV coordinates with time
      float gridSize = ${gridSize}.0;
      vec2 animatedUV = vUv + vec2(time * 0.1, time * 0.05); // Adjust speed multipliers to control animation speed
      vec2 grid = fract(animatedUV * vec2(gridSize, gridSize / ${grid}));
      
      // Calculate distance from center
      vec2 centered = vUv - vec2(0.5);
      float distFromCenter = length(centered);
      
      // Vary line width based on distance from center
      float baseWidth = ${baseWidth}; // Thinner base width
      float maxWidth = ${maxWidth}; // Thicker maximum width
      float lineWidth = mix(baseWidth, maxWidth, distFromCenter);
      float softness = 0.0001;
      
      // Draw grid lines with smooth edges using smoothstep
      float horizontalLine = smoothstep(0.0, softness, grid.y) - smoothstep(lineWidth - softness, lineWidth, grid.y);
      float verticalLine = smoothstep(0.0, softness, grid.x) - smoothstep(lineWidth - softness, lineWidth, grid.x);
      
      // Combine lines
      float gridPattern = max(horizontalLine, verticalLine);
      
      // Output final color with opacity
      gl_FragColor = vec4(color, gridPattern * ${opacity});
    }
  `}
      vertexShader={`
    varying vec2 vUv;
    
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1);
    }
  `}
      uniforms={{
        color: {
          value: [
            theme.palette.yellow.main
              .match(/\w\w/g)
              ?.map((x) => parseInt(x, 16) / 255)[0] ?? 0,
            theme.palette.yellow.main
              .match(/\w\w/g)
              ?.map((x) => parseInt(x, 16) / 255)[1] ?? 0,
            theme.palette.yellow.main
              .match(/\w\w/g)
              ?.map((x) => parseInt(x, 16) / 255)[2] ?? 0,
          ],
        },
        time: { value: 0 },
      }}
      transparent={true}
      side={FrontSide}
      onBeforeCompile={(shader) => {
        if (animationSpeed != null) {
          // Update time uniform every frame
          const animate = () => {
            shader.uniforms.time.value += animationSpeed;
            requestAnimationFrame(animate);
          };
          animate();
        }
      }}
    />
  );
};
